/**
 * The shape of one `feed` row as the admin console reads it, and the
 * per-type "Details" reader both the global Events tab and a single
 * venue's own "Coming up" table use.
 *
 * Split out of `EventsPanel.tsx` on 23 September, the same day a venue's
 * own panel grew a matching table (`VenuePanel.tsx`'s "Coming up" section):
 * two screens reading the same columns and printing the same per-type
 * fields is exactly the "kept in step by hand" shape `leadVenue.ts` already
 * warns about for its own `fold()` — worth a shared file, not a second
 * copy, once there were two readers rather than one.
 *
 * `FeedEvent` is `VenueEvent`, `app/src/lib/venueRecord.ts`'s own trimmed,
 * hand-written interface (not the generated `FeedRow` — see the note on
 * that type in `app/src/lib/types.ts`, and the one on `VenueEvent` itself
 * for why a venue's fetch and the global one now share this exact shape).
 * Re-exported under this name because every reader in this file was
 * written calling it `FeedEvent`, and a rename across two more files for a
 * name that means the same thing was not worth doing.
 */
import { fmt } from '../../app/src/lib/calendar'
import type { VenueEvent } from '../../app/src/lib/venueRecord'

export type FeedEvent = VenueEvent

/** Every column either reader needs — the same string `app/src/lib/
 *  venueRecord.ts`'s own `EVENT_FIELDS` holds, kept as a second literal
 *  rather than imported so `EventsPanel`'s `.select()` call still infers its
 *  row shape from a string *this* module owns (see the note there on why it
 *  must stay one un-concatenated literal, not built with `+`). A venue's own
 *  table additionally filters by `venue_id`/`building_id` server-side; the
 *  columns themselves are identical, which is the reason it must not drift. */
export const EVENT_FIELDS = 'id,title,type,discipline,run_start,run_end,published_on,venue,venue_id,building,district,is_free,price_min,price_max,price_note,english_access,event_lang,performers,film,next_showing,next_showing_venue,showing_count,opening_on,url,booking_url,status'

// ---------------------------------------------------------------------------
// Formatting — small and local, the same choice VenuePanel's own metadata
// fields already made rather than fighting the generated `FeedRow`'s full
// shape for a trimmed column list. `fmt` itself is shared, from
// `app/src/lib/calendar`.
// ---------------------------------------------------------------------------

export const fmtDate = (iso: string) => fmt(iso, { day: 'numeric', month: 'short', year: 'numeric' })
export const fmtDateShort = (iso: string) => fmt(iso, { day: 'numeric', month: 'short' })

/** For `next_showing`, a full timestamp — unlike `fmtDate`/`fmtDateShort`,
 *  which are for the bare `YYYY-MM-DD` dates `run_start`/`run_end` are.
 *  `fmt`'s own `D()` appends a bare noon UTC to whatever it is given, which
 *  turns an already-full timestamp into an unparseable string and prints
 *  "Invalid Date" — caught rendering this file's own preview harness when
 *  it was still part of `EventsPanel.tsx`. */
export const fmtTimestampShort = (at: string) =>
  new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/Berlin' })

function localTime(at: string | null): string | null {
  if (!at) return null
  const t = new Date(at).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Berlin',
  })
  return t === '00:00' || t === '24:00' ? null : t
}

export function whenText(e: FeedEvent): string {
  if (e.published_on) return fmtDate(e.published_on)
  if (!e.run_start) return 'No date given'
  const oneDay = !e.run_end || e.run_end === e.run_start
  if (oneDay) {
    const t = e.showing_count === 1 ? localTime(e.next_showing) : null
    return t ? `${fmtDate(e.run_start)}, ${t}` : fmtDate(e.run_start)
  }
  return `${fmtDateShort(e.run_start)} – ${fmtDate(e.run_end!)}`
}

export function priceText(e: FeedEvent): string | null {
  if (e.is_free) return 'Free'
  if (e.price_min != null && e.price_max != null && e.price_min !== e.price_max) return `€${e.price_min}–${e.price_max}`
  if (e.price_min != null) return `€${e.price_min}`
  return e.price_note
}

export const typeLabel = (t: string) => t.replace(/_/g, ' ')

const ENGLISH_LABEL: Record<string, string> = {
  surtitles: 'English surtitles', native: 'In English', not_applicable: 'No language barrier',
  none: 'German only', unknown: 'English access unknown',
}

/** One row of the Details column, or nothing when the field is empty — a
 *  blank `<dd>` reads as "this venue has no conductor," which is worse than
 *  omitting the line, the same reasoning `addressLine` in VenuePanel uses. */
export type Field = { label: string; value: string }
const field = (label: string, value: string | number | null | undefined): Field[] =>
  value == null || value === '' ? [] : [{ label, value: String(value) }]

/**
 * What is worth showing beyond title, venue and date — the fields that
 * actually vary by `type`. Reads `performers` and `film` directly rather
 * than the curated one-line `detailLine` the phone app's cards use: this is
 * an admin table and Doug's stated rule for the Record section applies here
 * too — show what the database actually holds, not a sentence built from it.
 *
 * Falls through to a generic reader for a `type` none of the branches name,
 * so a future recipe that invents a new one shows its `performers`/`film`
 * fields rather than an empty cell.
 */
export function expandedFields(e: FeedEvent): Field[] {
  const p = e.performers ?? {}
  const f = e.film ?? {}

  if (e.type === 'concert' || (e.type === 'performance' && e.discipline === 'opera')) {
    return [
      ...field('Conductor', p.conductor),
      ...field('Ensemble', p.ensemble),
      ...field('Soloists', p.soloists),
      ...field('Composer', p.composer),
      ...field('Programme', p.programme),
    ]
  }

  if (e.type === 'performance') {
    return [
      ...field('Director', p.director),
      ...field('Composer', p.composer),
      ...field('Language', e.english_access ? ENGLISH_LABEL[e.english_access] ?? e.english_access : null),
    ]
  }

  if (e.type === 'screening' || e.discipline === 'film') {
    const directors = (f.directors ?? []).join(', ')
    const genres = (f.genres ?? []).join(', ')
    const ratings = [
      typeof f.rt_score === 'number' ? `RT ${f.rt_score}%` : null,
      typeof f.metascore === 'number' ? `Metacritic ${f.metascore}` : null,
      typeof f.imdb_rating === 'number' ? `IMDb ${f.imdb_rating}` : null,
    ].filter(Boolean).join(' · ')
    return [
      ...field('Director', directors),
      ...field('Year', f.year),
      ...field('Genre', genres),
      ...field('Ratings', ratings),
      ...field('Next showing', e.next_showing_venue && e.next_showing
        ? `${e.next_showing_venue}, ${fmtTimestampShort(e.next_showing)}` : null),
      ...field('Showings', (e.showing_count ?? 0) > 1 ? `${e.showing_count} dates` : null),
    ]
  }

  if (e.type === 'exhibition' || e.type === 'opening') {
    return [
      ...field('Discipline', e.discipline),
      ...field('Opening', e.opening_on ? fmtDate(e.opening_on) : null),
      ...field('Director', p.director),
    ]
  }

  if (e.type === 'talk' || e.type === 'tour' || e.type === 'workshop') {
    return [
      ...field('Language', e.event_lang),
      ...field('Director', p.director),
    ]
  }

  // Anything else: whatever performers/film actually holds, key by key,
  // rather than a blank cell for a type this file has not been taught yet.
  return [
    ...Object.entries(p).map(([k, v]) => ({ label: k.replace(/_/g, ' '), value: v })),
    ...field('Year', f.year),
    ...field('Director', (f.directors ?? []).join(', ')),
  ]
}
